// client/src/coms/components/ServerRail.js
// Optional vertical server navigation. Shares its server list, badges, permissions and actions
// with the default dropdown (SidebarTopDrawer) through useServerNavModel.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LogoFill, ServerContextMenu, useServerNavModel } from './sidebar_parts/serverNavModel';

const MANUAL_LANDING_SESSION_KEY = 'voxemera_manual_landing_once';

function getPublicPath(p) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const path = String(p || '').startsWith('/') ? String(p || '') : `/${String(p || '')}`;
  return `${base}${path}`;
}

// Index to pass to moveServer when dragging the item at `fromIdx` onto the item at `overIdx`,
// dropping before it (placeAfter=false) or after it (placeAfter=true).
export function computeDropIndex(fromIdx, overIdx, placeAfter) {
  if (fromIdx < 0 || overIdx < 0) return -1;
  let target = placeAfter ? overIdx + 1 : overIdx;
  if (fromIdx < target) target -= 1;
  return target;
}

function describeServer(srv, badge) {
  const name = String((srv && srv.name) || 'Server');
  if (badge.showMention) return `${name}, ${badge.mentionCount} mention${badge.mentionCount === 1 ? '' : 's'}`;
  if (badge.showUnread) return `${name}, unread messages`;
  return name;
}

export default function ServerRail({
  servers,
  activeServerId,
  onSelectServer,
  onCreateServer,
  onServersChanged,
  onOpenServerSettings,
  apiAvailable,
  user,
  serverUnreadById,
  serverMentionCountById,
}) {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef(new Map());

  const nav = useServerNavModel({
    servers,
    activeServerId,
    onSelectServer,
    onServersChanged,
    apiAvailable,
    user,
    serverUnreadById,
    serverMentionCountById,
    rootRef,
  });

  const [focusId, setFocusId] = useState('');
  const [tooltip, setTooltip] = useState(null);
  const [dragId, setDragId] = useState('');
  const [dropHint, setDropHint] = useState(null);

  const list = nav.servers;
  const activeId = String(activeServerId || '');
  const voxLogoUrl = useMemo(() => getPublicPath('/vox-favicon-32x32.png'), []);
  const pointAtlasLogoUrl = useMemo(() => getPublicPath('/favicon-32x32.png'), []);

  // Roving tabindex: exactly one server button is in the tab order.
  const tabStopId = useMemo(() => {
    if (focusId && list.some((s) => s.id === focusId)) return focusId;
    if (list.some((s) => s.id === activeId)) return activeId;
    return list[0] ? list[0].id : '';
  }, [focusId, list, activeId]);

  useEffect(() => {
    const el = itemRefs.current.get(activeId);
    if (!el || typeof el.scrollIntoView !== 'function') return;
    try {
      el.scrollIntoView({ block: 'nearest' });
    } catch {}
  }, [activeId]);

  const { orderErr, clearOrderErr } = nav;
  useEffect(() => {
    if (!orderErr) return undefined;
    const t = setTimeout(clearOrderErr, 6000);
    return () => clearTimeout(t);
  }, [orderErr, clearOrderErr]);

  const focusServer = useCallback((sid) => {
    setFocusId(sid);
    const el = itemRefs.current.get(sid);
    if (el) {
      try {
        el.focus();
      } catch {}
    }
  }, []);

  const showTooltip = useCallback((e, label) => {
    const el = e && e.currentTarget;
    if (!el || typeof el.getBoundingClientRect !== 'function') return;
    const r = el.getBoundingClientRect();
    setTooltip({ label, left: Math.round(r.right + 12), top: Math.round(r.top + r.height / 2) });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const { moveServer, selectServer, openServerContextMenu } = nav;

  const onItemKeyDown = useCallback(
    (e, srv, idx) => {
      const key = e.key;

      if (e.altKey && e.shiftKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
        e.preventDefault();
        const target = key === 'ArrowUp' ? idx - 1 : idx + 1;
        if (target < 0 || target >= list.length) return;
        moveServer(srv.id, target);
        requestAnimationFrame(() => focusServer(srv.id));
        return;
      }

      let nextIdx = -1;
      if (key === 'ArrowDown') nextIdx = Math.min(list.length - 1, idx + 1);
      else if (key === 'ArrowUp') nextIdx = Math.max(0, idx - 1);
      else if (key === 'Home') nextIdx = 0;
      else if (key === 'End') nextIdx = list.length - 1;

      if (nextIdx >= 0 && list[nextIdx]) {
        e.preventDefault();
        focusServer(list[nextIdx].id);
        return;
      }

      if (key === 'F10' && e.shiftKey) {
        e.preventDefault();
        openServerContextMenu({ currentTarget: e.currentTarget, preventDefault() {}, stopPropagation() {} }, srv);
      }
    },
    [list, moveServer, focusServer, openServerContextMenu]
  );

  const onDragStart = useCallback(
    (e, srv) => {
      setDragId(srv.id);
      hideTooltip();
      nav.closeMenu();
      try {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', srv.id);
      } catch {}
    },
    [hideTooltip, nav]
  );

  const onDragOver = useCallback(
    (e, srv) => {
      if (!dragId) return;
      e.preventDefault();
      try {
        e.dataTransfer.dropEffect = 'move';
      } catch {}
      const r = e.currentTarget.getBoundingClientRect();
      const placeAfter = e.clientY > r.top + r.height / 2;
      setDropHint((prev) =>
        prev && prev.id === srv.id && prev.after === placeAfter ? prev : { id: srv.id, after: placeAfter }
      );
    },
    [dragId]
  );

  const endDrag = useCallback(() => {
    setDragId('');
    setDropHint(null);
  }, []);

  const onDrop = useCallback(
    (e, srv) => {
      e.preventDefault();
      const fromIdx = list.findIndex((s) => s.id === dragId);
      const overIdx = list.findIndex((s) => s.id === srv.id);
      const placeAfter = !!(dropHint && dropHint.id === srv.id && dropHint.after);
      const target = computeDropIndex(fromIdx, overIdx, placeAfter);
      const movedId = dragId;
      endDrag();
      if (target >= 0 && movedId) moveServer(movedId, target);
    },
    [list, dragId, dropHint, endDrag, moveServer]
  );

  const goVoxemera = useCallback(() => {
    try {
      window.sessionStorage.setItem(MANUAL_LANDING_SESSION_KEY, '1');
    } catch {}
    try {
      window.location.assign('/');
    } catch {}
  }, []);

  const goPointAtlas = useCallback(() => {
    try {
      window.location.assign('https://point-atlas.io/');
    } catch {}
  }, []);

  return (
    <nav ref={rootRef} className="coms-rail" aria-label="Servers">
      <div className="coms-rail-top">
        <button
          type="button"
          className="coms-rail-home"
          aria-label="Voxemera home"
          onClick={goVoxemera}
          onMouseEnter={(e) => showTooltip(e, 'Voxemera')}
          onMouseLeave={hideTooltip}
          onFocus={(e) => showTooltip(e, 'Voxemera')}
          onBlur={hideTooltip}
        >
          <LogoFill url={voxLogoUrl} alt="" size={22} />
        </button>

        <button
          type="button"
          className="coms-rail-home"
          aria-label="Point Atlas"
          onClick={goPointAtlas}
          onMouseEnter={(e) => showTooltip(e, 'Point Atlas')}
          onMouseLeave={hideTooltip}
          onFocus={(e) => showTooltip(e, 'Point Atlas')}
          onBlur={hideTooltip}
        >
          <LogoFill url={pointAtlasLogoUrl} alt="" size={22} />
        </button>

        <div className="coms-rail-divider" aria-hidden="true" />
      </div>

      <ul ref={listRef} className="coms-rail-list" onScroll={hideTooltip}>
        {list.map((srv, idx) => {
          const sid = srv.id;
          if (!sid) return null;

          const logo = nav.getServerLogo(srv);
          const isActive = activeId === sid;
          const badge = nav.getServerBadge(srv);
          const label = describeServer(srv, badge);
          const slotClass = [
            'coms-rail-slot',
            dropHint && dropHint.id === sid && dragId !== sid ? (dropHint.after ? 'drop-after' : 'drop-before') : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <li key={sid} className={slotClass}>
              {isActive || badge.showUnread ? (
                <span className={`coms-rail-pill${isActive ? ' active' : ''}`} aria-hidden="true" />
              ) : null}

              <button
                ref={(el) => {
                  if (el) itemRefs.current.set(sid, el);
                  else itemRefs.current.delete(sid);
                }}
                type="button"
                className={`coms-rail-item${isActive ? ' active' : ''}${dragId === sid ? ' dragging' : ''}`}
                aria-label={label}
                aria-current={isActive ? 'page' : undefined}
                aria-keyshortcuts="Alt+Shift+ArrowUp Alt+Shift+ArrowDown"
                tabIndex={tabStopId === sid ? 0 : -1}
                draggable
                onClick={() => {
                  setFocusId(sid);
                  selectServer(sid);
                }}
                onContextMenu={(e) => {
                  hideTooltip();
                  openServerContextMenu(e, srv);
                }}
                onKeyDown={(e) => onItemKeyDown(e, srv, idx)}
                onFocus={(e) => {
                  setFocusId(sid);
                  showTooltip(e, srv.name || 'Server');
                }}
                onBlur={hideTooltip}
                onMouseEnter={(e) => showTooltip(e, srv.name || 'Server')}
                onMouseLeave={hideTooltip}
                onDragStart={(e) => onDragStart(e, srv)}
                onDragOver={(e) => onDragOver(e, srv)}
                onDrop={(e) => onDrop(e, srv)}
                onDragEnd={endDrag}
              >
                {badge.showMention ? (
                  <span className="coms-rail-badge" aria-hidden="true">
                    {badge.mentionText}
                  </span>
                ) : badge.showUnread && !isActive ? (
                  <span className="coms-rail-dot" aria-hidden="true" />
                ) : null}

                {logo ? (
                  <LogoFill url={logo} alt="" onBroken={() => nav.markLogoBroken(sid)} />
                ) : (
                  <span aria-hidden="true">{String(srv.acronym || '?').slice(0, 3).toUpperCase()}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="coms-rail-bottom">
        {nav.orderErr ? (
          <div className="coms-rail-error" role="alert">
            {nav.orderErr}
          </div>
        ) : null}

        <button
          type="button"
          className="coms-rail-item add"
          aria-label="Create server"
          onClick={() => {
            nav.closeMenu();
            if (typeof onCreateServer === 'function') onCreateServer();
          }}
          onMouseEnter={(e) => showTooltip(e, 'Create server')}
          onMouseLeave={hideTooltip}
          onFocus={(e) => showTooltip(e, 'Create server')}
          onBlur={hideTooltip}
        >
          +
        </button>
      </div>

      {tooltip && !dragId && !nav.menu.open ? (
        <div className="coms-rail-tooltip" role="tooltip" style={{ left: tooltip.left, top: tooltip.top }}>
          {tooltip.label}
        </div>
      ) : null}

      <ServerContextMenu
        nav={nav}
        activeServerId={activeServerId}
        onOpenServerSettings={onOpenServerSettings}
        onMoveServer={moveServer}
      />
    </nav>
  );
}
